import {
  Accordion,
  Button,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAddressBook,
  IconChartBarPopular,
  IconClipboardList,
  IconDeviceFloppy,
  IconDownload,
  IconEdit,
  IconHistory,
  IconInfoCircle,
  IconNote,
  IconPackage,
  IconPlus,
  IconPrinter,
  IconUser,
} from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import {
  getDebtor,
  getDebtorOrderHistories,
  requestNewIdCheck,
  submitDebtor,
} from "api/debtor.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MAX_PAGE_SIZE,
  MSG_DEFAULT_ERROR_TITLE,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import SafeHtml from "components/SafeHtml";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";
import React, { startTransition, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Debtor } from "types/model.types";

import { useForm } from "@mantine/form";
import AdminDebtorOrderHistory from "./AdminDebtorOrderHistory";
import AdminDebtorBackOrder from "./AdminOrderBackOrder";
import AdminDebtorTransactions from "./AdminOrderTransactions";

import { getParentStatements } from "api/statement.service";
import CustomPageNotification from "components/layouts/CustomPageNotification";
import AdminStatementAndInvoicesPreview from "pages/admin/reports/AdminStatementAndInvoicesPreview";
import CustomerDetailsCard1 from "./cards/CustomerDetailsCard1";
import CustomerDetailsCard2 from "./cards/CustomerDetailsCard2";
import CustomerDetailsCard3 from "./cards/CustomerDetailsCard3";
import FinancialSummaryCard from "./cards/FinancialSummaryCard";
import SalesSummaryCard from "./cards/SalesSummaryCard";

const AdminDebtorEnquiryComponent: React.FC = () => {
  const [params] = useSearchParams();
  const [opened, setOpened] = useState(false);
  const [includeInvoices, setIncludeInvoices] = useState(false);
  const id = params.get("id");
  const { debtors } = useProgressiveDebtorFetch();
  const [readonly, setReadonly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("details");
  const [reset, setReset] = useState<Date>(new Date());
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const navigate = useNavigate();
  const { refresh } = useProgressiveDebtorFetch();

  // Memoize form instance so it never recreates on scroll
  const initialValues = useMemo(
    () => ({
      id: 0,
      requestedNewId: 0,
      initials: "",
      surname: "",
      alpha: "",
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      phone: "",
      attn1: "",
      attn2: "",
      sellPriceLevel: 1,
      opening: 0,
      currentMonth: 0,
      month1: 0,
      month2: 0,
      month3: 0,
      balance: 0,
      gst: 0,
      discount: 0,
      salesMtd: 0,
      salesYtd: 0,
      cashMtd: 0,
      cashYtd: 0,
      gstMtd: 0,
      gstYtd: 0,
      lastSale: "",
      lastAged: 0,
      deliveryAddressLine1: "",
      deliveryAddressLine2: "",
      deliveryAddressLine3: "",
      faxNumber: "",
      parentDebtorId: 0,
      mob: "",
      overdue: false,
      overdueOverride: false,
      salesRep: "",
      excludeInvoiceFromEom: false,
      groupStatementAndInvoices: false,
      cashSale: false,
      alternateEmail: "",
      email: "",
      specialNotes: "",
      excludeInvAndStatementFromEom: false,
      active: true,
    }),
    [],
  );
  const maxLen = (len: number) => (value: string) =>
    value && value.length > len ? `Max length is ${len}` : null;

  const isDate = (value: string) =>
    value && isNaN(Date.parse(value)) ? "Invalid date" : null;

  const form = useForm<Debtor>({
    mode: "controlled",
    initialValues,
    validate: {
      initials: maxLen(255),
      surname: maxLen(255),
      alpha: maxLen(255),

      addressLine1: maxLen(255),
      addressLine2: maxLen(255),
      addressLine3: maxLen(255),

      attn1: maxLen(255),
      attn2: maxLen(255),

      deliveryAddressLine1: maxLen(255),
      deliveryAddressLine2: maxLen(255),
      // deliveryAddressLine3 is nvarchar(max) → no validation needed

      faxNumber: maxLen(50),
      mob: maxLen(50),

      email: maxLen(255),
      alternateEmail: maxLen(150),

      salesRep: maxLen(80),

      // date fields
      lastSale: isDate,

      // numeric fields (optional — Mantine handles most)
      opening: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      currentMonth: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      month1: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      month2: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      month3: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      balance: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      gst: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      discount: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      salesMtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      salesYtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      cashMtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      cashYtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      gstMtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      gstYtd: (v) => (isNaN(Number(v)) ? "Invalid number" : null),

      // ints
      lastAged: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
      parentDebtorId: (v) => (isNaN(Number(v)) ? "Invalid number" : null),
    },
  });

  // Extract stable helpers from form
  const { values, setFieldValue, getInputProps } = form;

  // Memoize the props passed to children so they stay stable
  const childFormApi = useMemo(
    () => ({
      values,
      setFieldValue,
      getInputProps,
    }),
    [values],
  ); // only changes when actual values change

  useEffect(() => {
    if (id) return;

    if (!debtors || debtors.length === 0) return;
    if (debtors && debtors.length > 0) {
      const d = debtors[0];
      if (d) navigate(`/admin/enquire-debtors?id=${d.id}`);
    }
  }, [debtors.length]);

  // Load recordId
  useEffect(() => {
    if (!id) return;

    const newId = Number(id);
    if (newId === 0) {
      setRecordId(undefined); // or null
      form.setValues(initialValues);
      form.resetDirty(); // important: clears dirty state
      return;
    }

    if (!readonly && recordId !== undefined && newId !== recordId) {
      notifications.show({
        title: MSG_DEFAULT_ERROR_TITLE,
        message:
          "You are in edit mode. Please save existing data before loading different record(s)",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    setRecordId(newId);
  }, [id, readonly, recordId, reset]);

  // Load debtor data
  useEffect(() => {
    if (!recordId) return;

    let mounted = true;

    const fetchDebtor = async () => {
      const response = await safeApiCall(getDebtor(recordId));
      if (response && mounted) {
        startTransition(() => {
          requestAnimationFrame(() => {
            form.setValues(response.data);
            form.resetDirty();
          });
        });
      }
    };

    fetchDebtor();
    return () => {
      mounted = false;
    };
  }, [recordId, reset]);

  // Memoized HTML
  // const instructionsHtml = useMemo(
  //   () => <SafeHtml html={form.values.specialNotes ?? ""} />,
  //   [form.values.specialNotes],
  // );
  const instructionsHtml = useMemo(() => {
    const raw = form.values.specialNotes ?? "";

    // Detect if it's already HTML (very simple heuristic)
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);

    const normalized = looksLikeHtml ? raw : raw.replace(/\r?\n/g, "<br />");

    return <SafeHtml html={normalized} />;
  }, [form.values.specialNotes]);

  if (!id) {
    return <Text>Please select a customer from the search screen.</Text>;
  }

  const handleAdd = async () => {
    setRecordId(0);

    setTimeout(() => {
      navigate("/admin/enquire-debtors?id=0", { replace: true });
    }, 500);

    setRecordId(0);
    startTransition(() => {
      requestAnimationFrame(() => {
        form.setValues(initialValues);
        setReadonly(false);
      });
    });
  };

  const handleSubmit = async () => {
    const validation = form.validate();
    if (validation.hasErrors) {
      notifications.show({
        title: "Validation Error",
        message: "Please fix the highlighted fields.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    setSaving(true);

    const payload = form.getValues();
    let isNewCustomer = false;

    if (Number(id ?? 0) === 0) {
      isNewCustomer = true;
      // new customer -
      const idCheckResponse = await safeApiCall(
        requestNewIdCheck(Number(payload.requestedNewId ?? 0)),
      );
      if (idCheckResponse && idCheckResponse.error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_ERR,
          message: idCheckResponse.error ?? "",
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
        setSaving(false);
        return;
      }
    }

    try {
      const response = await safeApiCall(submitDebtor(payload));
      if (!response) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_ERR,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
        return;
      }

      if (isNewCustomer) {
        refresh();
      }

      // Return to readonly mode
      startTransition(() => {
        setReadonly(true);
      });

      const responseId: number = response?.id;
      if (responseId !== Number(id)) {
        setTimeout(() => {
          navigate(`/admin/enquire-debtors?id=${responseId}&r=1`, {
            replace: true,
          });
        }, 30);
        return;
      }

      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (error) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadOrderHistory = async () => {
    try {
      await safeApiCall(
        getDebtorOrderHistories(Number(id), 1, MAX_PAGE_SIZE, true),
      );
      notifications.show({
        title: "File Download!",
        message: `Downloading CSV report`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (err) {
      notifications.show({
        title: "File Download!",
        message: `Unable to export CSV for this report. ${err}`,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    }
  };
  const handleDownloadStatements = async () => {
    try {
      await safeApiCall(getParentStatements(Number(id)));
      notifications.show({
        title: "File Download!",
        message: `Downloading CSV report`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (err) {
      notifications.show({
        title: "File Download!",
        message: `Unable to export CSV for this report. ${err}`,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    }
  };
  const handlePrint = (includeInvoice = false) => {
    navigateTo(
      `/print/statement-invoices?id=${id}&i=${includeInvoice ? "1" : "0"}`,
    );
  };

  const getAlertMessage = () => {
    if (Boolean(values.active) === false) {
      return "This account is currently in-active.";
    }
    if (
      Boolean(values.cashSale) === true &&
      (Boolean(values.overdue) === true ||
        Boolean(values.overdueOverride) === true)
    ) {
      return "CASH ACCOUNT OVERDUE";
    }
    if (Boolean(values.cashSale) === true) {
      return "CASH SALE ACCOUNT";
    }
    if (
      Boolean(values.overdue) === true ||
      Boolean(values.overdueOverride) === true
    ) {
      return "ACCOUNT OVERDUE";
    }
    return "";
  };

  const message = getAlertMessage();

  return (
    <Stack>
      <Group justify="space-between" mb="md" wrap="nowrap">
        {/* LEFT SIDE BUTTONS */}
        <Group justify="flex-start" wrap="nowrap" mb="md">
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAdd}
            disabled={saving || !readonly}
            title="Under Construction"
          >
            New Customer
          </Button>

          <Button
            disabled={saving || values.id === undefined}
            leftSection={
              readonly ? <IconEdit size={16} /> : <IconDeviceFloppy size={16} />
            }
            onClick={
              readonly
                ? () => {
                    startTransition(() => {
                      setReadonly(false);
                      setActiveTab("details");
                    });
                  }
                : handleSubmit
            }
          >
            {readonly ? "Edit Customer" : "Save Customer Details"}
          </Button>
          <Button
            disabled={saving || readonly}
            leftSection={
              readonly ? <IconEdit size={16} /> : <IconDeviceFloppy size={16} />
            }
            onClick={() => {
              setReset(new Date());
              setReadonly(true);
            }}
          >
            Cancel Edit
          </Button>
        </Group>
        {/* right side */}
        {/* RIGHT SIDE BUTTONS */}
        <Group justify="flex-end" wrap="nowrap">
          <Button
            variant="light"
            leftSection={<IconPrinter size={16} />}
            onClick={() => {
              //setIncludeInvoices(false);
              //setOpened(true);
              handlePrint(false);
            }}
          >
            Statement
          </Button>
          <Button
            variant="light"
            leftSection={<IconPrinter size={16} />}
            onClick={() => {
              // setIncludeInvoices(true);
              // setOpened(true);
              handlePrint(true);
            }}
          >
            Statement and Invoices
          </Button>

          <Button
            variant="outline"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadStatements}
          >
            EOM Statements
          </Button>

          <Button
            variant="outline"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadOrderHistory}
          >
            Order History
          </Button>
        </Group>
      </Group>

      {message && (
        <CustomPageNotification title="Account Status" message={message} />
      )}

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="details" leftSection={<IconUser size={14} />}>
            Customer Details
          </Tabs.Tab>

          <Tabs.Tab
            disabled={!readonly}
            value="transactions"
            leftSection={<IconClipboardList size={14} />}
          >
            Transactions
          </Tabs.Tab>

          <Tabs.Tab
            value="backorders"
            disabled={!readonly}
            leftSection={<IconPackage size={14} />}
          >
            Back Orders
          </Tabs.Tab>

          <Tabs.Tab
            value="history"
            disabled={!readonly}
            leftSection={<IconHistory size={14} />}
          >
            Order History
          </Tabs.Tab>

          <Tabs.Tab
            value="instructions"
            disabled={!readonly}
            leftSection={<IconNote size={14} />}
          >
            Special Instructions
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <Accordion multiple={false} defaultValue="card1">
            <Accordion.Item value="card1">
              <Accordion.Control icon={<IconInfoCircle size={16} />}>
                Customer Details: {id ? `#${id}` : "New Customer"}
              </Accordion.Control>
              <Accordion.Panel>
                <CustomerDetailsCard1
                  readonly={readonly}
                  formApi={childFormApi}
                />
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="card2">
              <Accordion.Control icon={<IconAddressBook size={16} />}>
                Addresses & Contact Details
              </Accordion.Control>
              <Accordion.Panel>
                <CustomerDetailsCard2
                  readonly={readonly}
                  formApi={childFormApi}
                />
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="card3">
              <Accordion.Control icon={<IconChartBarPopular size={16} />}>
                Financials & Sales Data
              </Accordion.Control>
              <Accordion.Panel>
                <FinancialSummaryCard
                  readonly={readonly}
                  formApi={childFormApi}
                />
                <SalesSummaryCard readonly={readonly} formApi={childFormApi} />
              </Accordion.Panel>
            </Accordion.Item>
            {!readonly && (
              <Accordion.Item value="card4">
                <Accordion.Control>Special Instructions</Accordion.Control>
                <Accordion.Panel>
                  <CustomerDetailsCard3
                    readonly={readonly}
                    formApi={childFormApi}
                  />
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>
        </Tabs.Panel>

        <Tabs.Panel value="transactions">
          <AdminDebtorTransactions />
        </Tabs.Panel>

        <Tabs.Panel value="backorders">
          <AdminDebtorBackOrder />
        </Tabs.Panel>

        <Tabs.Panel value="history">
          <AdminDebtorOrderHistory />
        </Tabs.Panel>

        <Tabs.Panel value="instructions">
          <Stack p="sm">{instructionsHtml}</Stack>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        fullScreen
        title="Statement and Invoice Preview"
        size="xl"
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        {id && (
          <AdminStatementAndInvoicesPreview
            id={Number(id)}
            includeInvoices={includeInvoices}
          />
        )}
      </Modal>
    </Stack>
  );
};

export const AdminDebtorEnquiry = React.memo(AdminDebtorEnquiryComponent);
export default AdminDebtorEnquiry;
